import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { CircleQuestionMarkIcon, XIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { LabelItem } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { cx, styled } from "simplydo/react/styled";
import { mobileOnly, useTheme } from "simplydo/react/theme";
import { Dropdown, type DropdownOption } from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import SlidingPane from "../SlidingModal";
import type {
  ChallengeIdeasResponse,
  ContentWrapperProps,
  FilterGroupOption,
  FilterParameterField,
  IdeaFilterUpdate,
  ProjectBoardLane,
} from "./types";

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  .filter-info {
    font-weight: bold;
    margin-bottom: 10px;
  }
  ${mobileOnly} {
    padding: 10px;
  }
`;

const RowFlex = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  margin-top: 15px;
  margin-bottom: 3px;
  margin-left: 10px;
  font-weight: bold;
`;

const ListItem = styled.div`
  display: flex;
  flex: 1;
  max-width: 100%;
  padding: 5px 10px 3px 10px;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: #d2dde5;
  }
  &.is-selected {
    background: #d2dde5;
    &.is-clearable:hover {
      background: #c2cdd5;
    }
  }

  > .filter-option-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    max-width: 100%;

    > .filter-option {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      overflow: hidden;
      gap: 10px;

      > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 250px;
      }
    }
  }
`;

const definedOptions = <T,>(options: Array<T | undefined>): T[] =>
  options.filter((option): option is T => option != null);

// Issue with dropdown in popup content, therefore we need to render the popup content directly
const ContentWrapper = ({
  drawPopup,
  trigger,
  ...props
}: ContentWrapperProps) => {
  if (drawPopup) {
    return <Popup trigger={trigger} {...props} />;
  }
  return <>{trigger}</>;
};

type SelectableListOption = DropdownOption & {
  tooltip?: string;
  values?: string[];
  type?: string;
};

type SelectableListProps = {
  options: SelectableListOption[];
  selected?: string;
  onChange: (option: SelectableListOption) => void;
  clearable?: boolean;
  style?: React.CSSProperties;
  onOptionChange?: (options: string[]) => void;
  selectedValues?: string[];
};

const SelectableList = ({
  options,
  selected,
  onChange,
  clearable,
  style = {},
  onOptionChange,
  selectedValues = [],
}: SelectableListProps) => {
  const [expanded, setExpanded] = useState(options.length < 5 || clearable);

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        gap: 2,
        ...style,
      }}
    >
      {(expanded ? options : options.slice(0, 5)).map((option) => (
        <ListItem
          key={option.value as string}
          className={cx(
            (clearable || option.value === selected) && "is-selected",
            clearable && "is-clearable",
          )}
          onClick={() => {
            onChange(option);
          }}
        >
          <ContentWrapper
            drawPopup={!option.values}
            disabled={
              !option.tooltip &&
              (typeof option.text !== "string" ||
                !option.text.length ||
                option.text.length < 25)
            }
            position="right center"
            offset={[-1, 10]}
            trigger={
              <div className="filter-option-wrapper">
                <div className="filter-option">
                  <span>{option.text}</span>
                  {clearable ? <Icon icon={XIcon} color="grey" /> : null}
                </div>
                {option.values ? (
                  <Dropdown
                    fluid
                    value={
                      selectedValues.find((c) =>
                        c.startsWith(option.value as string),
                      ) ?? ""
                    }
                    placeholder="Select a value"
                    onChange={(e, { value }) => {
                      const otherChoices = selectedValues.filter(
                        (choice) => !choice.startsWith(option.value as string),
                      );
                      onOptionChange?.(
                        value
                          ? [...otherChoices, value as string]
                          : otherChoices,
                      );
                    }}
                    options={option.values.map((opt: string) => ({
                      key: `${option.value}+${opt}`,
                      value: `${option.value}+${opt}`,
                      text: opt,
                    }))}
                  />
                ) : null}
              </div>
            }
          >
            {option.tooltip ?? option.text}
          </ContentWrapper>
        </ListItem>
      ))}
      {!clearable && options.length > 5 ? (
        <ListItem
          onClick={() => setExpanded((prev) => !prev)}
          style={{ color: "#2185d0" }}
        >
          {expanded ? "- Show less" : "+ Show more"}
        </ListItem>
      ) : null}
    </div>
  );
};

type ChallengeIdeasResponseAlias = ChallengeIdeasResponse;

type ChallengeIdeaFiltersProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  popularTags?: OpenAPI.Schemas["Tag"][];
  allLabels?: string[];
  availableGroups?: ChallengeIdeasResponseAlias["availableGroups"];
  availableOrganisations?: ChallengeIdeasResponseAlias["availableOrganisations"];
  availableSortParameters: ChallengeIdeasResponseAlias["availableSortParameters"];
  availableFilterParameters: ChallengeIdeasResponseAlias["availableFilterParameters"];
  availableImpacts?: ChallengeIdeasResponseAlias["availableImpacts"];
  allProjectAssignees?: OpenAPI.Schemas["User"][];
};

function ChallengeIdeaFilters({
  challenge,
  popularTags = [],
  allLabels = [],
  availableGroups = [],
  availableOrganisations = [],
  availableSortParameters = {},
  availableFilterParameters = {},
  availableImpacts = [],
  allProjectAssignees = [],
}: ChallengeIdeaFiltersProps) {
  const dispatch = useAppDispatch();
  const {
    tagFilter,
    ideaIncludes,
    ideaOrder,
    groupFilter,
    orgFilter,
    labelsFilter,
    ideaOrderDirection,
    ideaFieldChoices: fieldChoices,
    adminHasCommented,
    projectBoardFilter,
    filtersVisible,
    ideaViewType,
    projectBoardLanesFilter,
    impactFilter,
  } = useAppSelector((state) => state.challenges);
  const user = useAppSelector((state) => state.user);
  const updateFilters = useCallback(
    (data: IdeaFilterUpdate) =>
      dispatch(actions.challenges.updateIdeaFilters(data)),
    [dispatch],
  );
  const { t } = useTranslation();

  const ideaFilters = [
    { type: "default", label: "generic.default" },
    { type: "name", label: "order.name" },
    { type: "voteCount", label: "order.votes", showIfVotingEnabled: true },
    { type: "completeness", label: "order.completeness" },
    { type: "updatedAt", label: "order.updatedAt" },
    { type: "createdAt", label: "order.createdAt" },
    { type: "submittedAt", label: "order.submittedAt", challengeManager: true },
    {
      type: "assessmentScore",
      label: "order.assessmentScore",
      challengeManager: true,
    },
    { type: "score", label: "order.smartScore", challengeManager: true },
  ];

  const theme = useTheme();

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );

  const challengeId = challenge?._id;
  const defaultLaneName = challenge?.projectBoard?.defaultLane?.name;
  const [allChallengeProjectLanes, setAllChallengeProjectLanes] = useState<
    ProjectBoardLane[]
  >([]);
  const getChallengeProjectLanes = useCallback(() => {
    api.boards.getProjectLanes(
      challengeId,
      (data) => {
        setAllChallengeProjectLanes([
          { _id: "default", name: defaultLaneName ?? "Default" },
          ...data.lanes,
        ]);
      },
      () => {},
    );
  }, [challengeId, defaultLaneName]);
  useEffect(() => {
    if (filtersVisible) {
      getChallengeProjectLanes();
    }
  }, [getChallengeProjectLanes, filtersVisible]);

  const [selectedFilterParameters, setSelectedFilterParameters] = useState<
    string[]
  >([]);
  const [usedFilterParameters, unusedFilterParameters] = useMemo(() => {
    const nextUsedFilterParameters: FilterParameterField[] = [];
    const nextUnusedFilterParameters: FilterParameterField[] = [];

    availableFilterParameters?.fields?.forEach((param) => {
      if (selectedFilterParameters.includes(param._id ?? "")) {
        nextUsedFilterParameters.push(param as FilterParameterField);
      } else {
        nextUnusedFilterParameters.push(param as FilterParameterField);
      }
    });

    return [nextUsedFilterParameters, nextUnusedFilterParameters];
  }, [selectedFilterParameters, availableFilterParameters]);

  const extraSortOptions = useMemo(() => {
    const { sliders = [] } = availableSortParameters;

    return sliders.map((param) => ({
      key: `${param.value}`,
      value: `${param.value}`,
      text: `Slider: ${param.name}`,
    }));
  }, [availableSortParameters]);

  const filterProjectAssignment = useMemo(
    () => [
      { key: "assignedToMe", value: "assignedToMe", text: "Assigned to me" },
      {
        key: "unassigned",
        value: "unassigned",
        text: "Not assigned to anyone",
      },
      ...allProjectAssignees.map((assignee) => ({
        key: assignee._id,
        value: `assignedTo+${assignee._id}`,
        text: `Assigned to ${assignee.profile.fullName}`,
      })),
    ],
    [allProjectAssignees],
  );

  if (
    user &&
    (challenge?.ideaVisibility === "users" ||
      canManageChallenge ||
      util.hasPermission(user, "challenge.viewIdeas", challenge?._id))
  ) {
    const sortOptions = [
      ...ideaFilters
        .filter(
          (f) =>
            !(
              f.challengeManager &&
              !(
                canManageChallenge ||
                util.hasPermission(user, "challenge.viewIdeas", challenge?._id)
              )
            ),
        )
        .filter(
          (f) =>
            !(
              f.showIfVotingEnabled &&
              !canManageChallenge &&
              !util.hasPermission(
                user,
                "challenge.viewIdeas",
                challenge?._id,
              ) &&
              challenge?.voteCountVisibility !== "users"
            ),
        )
        .map((f) => ({ key: f.type, text: t(f.label), value: f.type })),
      ...extraSortOptions,
    ];

    const filterTags = popularTags.map((pTag) => ({
      key: pTag._id,
      value: pTag._id,
      text: pTag.value,
    }));

    const filterGroups: FilterGroupOption[] = availableGroups.map((group) => ({
      key: group._id ?? "",
      text: group.name,
      value: group._id,
      type: "group",
    }));

    const filterOrganisations = availableOrganisations.map((organisation) => ({
      key: organisation._id,
      text: organisation.name,
      value: organisation._id,
      type: "organisation",
    }));

    const filterLabels = allLabels.map((label) => ({
      key: label,
      value: label,
      text: <LabelItem label={label} />,
    }));

    const filterAuthors: FilterGroupOption[] =
      filterOrganisations.length > 1
        ? filterGroups.concat([...filterOrganisations] as FilterGroupOption[])
        : filterGroups;

    const impactOptions = availableImpacts.map((impact) => ({
      key: impact._id,
      value: impact._id,
      text: impact.name,
    }));

    return (
      <SlidingPane
        isOpen={filtersVisible}
        from="left"
        width={400}
        onClose={() => updateFilters({ filtersVisible: false })}
      >
        <div style={{ padding: 16, flex: 1 }}>
          <div
            style={{ color: "#2185d0", cursor: "pointer" }}
            onClick={() => {
              updateFilters({ filtersVisible: false });
            }}
          >
            {"<"} Hide filters
          </div>
          <div>
            <OptionsContainer>
              <RowFlex>
                {ideaViewType !== "board" ? (
                  <>
                    <SectionHeader>Sort</SectionHeader>
                    <SelectableList
                      selected={ideaOrder}
                      options={sortOptions}
                      onChange={({ value }) =>
                        updateFilters({ ideaOrder: value })
                      }
                    />
                    <Dropdown
                      fluid
                      variant="outline"
                      clearable={false}
                      value={ideaOrderDirection}
                      style={{ marginTop: 5 }}
                      options={[
                        { key: "asc", value: "asc", text: "Sort Ascending" },
                        { key: "desc", value: "desc", text: "Sort Descending" },
                      ]}
                      onChange={(e, { value }) =>
                        updateFilters({ ideaOrderDirection: value })
                      }
                    />
                  </>
                ) : null}

                {canManageChallenge ||
                (challenge.ideaVisibility === "users" &&
                  !challenge.ideaVisibilityLimits) ||
                util.hasPermission(
                  user,
                  "challenge.viewIdeas",
                  challenge?._id,
                ) ||
                util.hasPermission(
                  user,
                  "challenge.viewProjectBoard",
                  challenge?._id,
                ) ? ( // Project board auto-enables submitted filter so needs to be able to disable this
                  <>
                    <SectionHeader>Status</SectionHeader>
                    <SelectableList
                      selected={ideaIncludes || "all"}
                      options={[
                        { key: "all", value: "all", text: "All" },
                        { key: "draft", value: "draft", text: "Draft" },
                        {
                          key: "submitted",
                          value: "submitted",
                          text: "Submitted",
                        },
                      ]}
                      onChange={({ value }) =>
                        updateFilters({
                          ideaIncludes: value === "all" ? null : value,
                        })
                      }
                    />
                  </>
                ) : null}

                {availableImpacts.length ? (
                  <>
                    <SectionHeader>
                      {t("common:capitalise", { key: "generic.impact" })}
                    </SectionHeader>
                    <Dropdown
                      fluid
                      search
                      variant="outline"
                      value={[]}
                      placeholder={`Search ${t("generic.impacts")}`}
                      onChange={(e, { value }) =>
                        updateFilters({
                          impactFilter: [...impactFilter, value],
                        })
                      }
                      options={impactOptions}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={impactOptions.filter((impact) =>
                        impactFilter.includes(impact.key),
                      )}
                      onChange={({ value }) => {
                        updateFilters({
                          impactFilter: impactFilter.filter(
                            (impact) => impact !== value,
                          ),
                        });
                      }}
                    />
                  </>
                ) : null}

                {popularTags?.length ? (
                  <>
                    <SectionHeader>Tags</SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      search
                      value={[]}
                      placeholder="Search tags..."
                      onChange={(e, { value }) =>
                        updateFilters({ tagFilter: [...tagFilter, value] })
                      }
                      options={filterTags.filter(
                        (tag) => !tagFilter.includes(tag.value),
                      )}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={definedOptions(
                        tagFilter.map((tag) =>
                          filterTags.find((pTag) => pTag.value === tag),
                        ),
                      )}
                      onChange={({ value }) =>
                        updateFilters({
                          tagFilter: tagFilter.filter((tag) => tag !== value),
                        })
                      }
                    />
                  </>
                ) : null}
                {(availableGroups?.length > 1 ||
                  availableOrganisations?.length > 1) &&
                (canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.viewIdeas",
                    challenge?._id,
                  )) ? (
                  <>
                    <SectionHeader>Author groups</SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      value={[]}
                      search
                      placeholder="Search groups..."
                      onChange={(e, { value }) => {
                        if (!value) return;
                        const option = filterAuthors.find(
                          (author) => author.value === value,
                        );
                        if (!option) return;
                        updateFilters(
                          option.type === "group"
                            ? { groupFilter: [...groupFilter, option.value] }
                            : { orgFilter: [...orgFilter, option.value] },
                        );
                      }}
                      options={
                        filterAuthors.filter(
                          (author) =>
                            !(
                              author.value && groupFilter.includes(author.value)
                            ) &&
                            !(author.value && orgFilter.includes(author.value)),
                        ) as (DropdownOption & { key: string })[]
                      }
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={definedOptions(
                        groupFilter
                          .concat(orgFilter)
                          .map((a) =>
                            filterAuthors.find((pTag) => pTag.value === a),
                          ),
                      )}
                      onChange={({ value, type }) =>
                        updateFilters(
                          type === "group"
                            ? {
                                groupFilter: groupFilter.filter(
                                  (g) => g !== value,
                                ),
                              }
                            : {
                                orgFilter: orgFilter.filter((o) => o !== value),
                              },
                        )
                      }
                    />
                  </>
                ) : null}

                {allLabels.length ? (
                  <>
                    <SectionHeader>Labels</SectionHeader>
                    <Dropdown
                      fluid
                      value={[]}
                      variant="outline"
                      placeholder="Search label..."
                      onChange={(e, { value }) =>
                        updateFilters({
                          labelsFilter: [...labelsFilter, value],
                        })
                      }
                      options={filterLabels}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={definedOptions(
                        labelsFilter.map((tag) =>
                          filterLabels.find((pTag) => pTag.value === tag),
                        ),
                      )}
                      onChange={({ value }) =>
                        updateFilters({
                          labelsFilter: labelsFilter.filter(
                            (tag) => tag !== value,
                          ),
                        })
                      }
                    />
                  </>
                ) : null}

                {canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.viewProjectBoard",
                  challenge?._id,
                ) ||
                challenge?.projectManagementVisibility === "users" ? (
                  <>
                    <SectionHeader>Project assignment</SectionHeader>
                    <Dropdown
                      fluid
                      value={[]}
                      search
                      variant="outline"
                      placeholder="Search assignee..."
                      options={filterProjectAssignment}
                      onChange={(e, { value }) => {
                        if (!value) return;
                        const option = filterProjectAssignment.find(
                          (assignee) => assignee.value === value,
                        );
                        if (!option) return;
                        updateFilters({
                          projectBoardFilter: [
                            ...projectBoardFilter,
                            option.value,
                          ],
                        });
                      }}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={filterProjectAssignment.filter((assignee) =>
                        projectBoardFilter.includes(
                          assignee.value as (typeof projectBoardFilter)[number],
                        ),
                      )}
                      onChange={({ value }) =>
                        updateFilters({
                          projectBoardFilter: projectBoardFilter.filter(
                            (assignee) => assignee !== value,
                          ),
                        })
                      }
                    />

                    <SectionHeader>Project lane</SectionHeader>
                    <Dropdown
                      fluid
                      variant="outline"
                      value={[]}
                      search
                      placeholder="Search lane..."
                      onChange={(e, { value }) =>
                        updateFilters({
                          projectBoardLanesFilter: [
                            ...projectBoardLanesFilter,
                            value,
                          ],
                        })
                      }
                      options={allChallengeProjectLanes.map((lane) => ({
                        key: lane._id,
                        value: lane._id,
                        text: lane.name,
                      }))}
                    />
                    <SelectableList
                      style={{ marginTop: 5 }}
                      clearable
                      options={allChallengeProjectLanes
                        .filter((lane) =>
                          projectBoardLanesFilter.includes(lane._id),
                        )
                        .map((lane) => ({
                          key: lane._id,
                          value: lane._id,
                          text: lane.name,
                        }))}
                      onChange={({ value }) =>
                        updateFilters({
                          projectBoardLanesFilter:
                            projectBoardLanesFilter.filter(
                              (lane) => lane !== value,
                            ),
                        })
                      }
                    />
                  </>
                ) : null}

                {util.canManageChallenge(user, challenge) ||
                util.hasPermission(
                  user,
                  "challenge.manageIdeaComments",
                  challenge?._id,
                ) ? (
                  <>
                    <SectionHeader>Admin comments</SectionHeader>
                    <SelectableList
                      selected={adminHasCommented || "all"}
                      options={[
                        { key: "all", value: "all", text: "All" },
                        {
                          key: "commented",
                          value: "commented",
                          text: "Only with",
                        },
                        {
                          key: "notCommented",
                          value: "notCommented",
                          text: "Only without",
                        },
                      ]}
                      onChange={({ value }) =>
                        updateFilters({
                          adminHasCommented: value === "all" ? null : value,
                        })
                      }
                    />
                  </>
                ) : null}

                {unusedFilterParameters.length ||
                usedFilterParameters.length ? (
                  <>
                    <Popup
                      trigger={
                        <SectionHeader>
                          Content{" "}
                          <Icon icon={CircleQuestionMarkIcon} size="small" />
                        </SectionHeader>
                      }
                      position="right center"
                      offset={[-1, 10]}
                    >{`Filter ${t("generic.ideas")} by the content in their responses.`}</Popup>
                    <Dropdown
                      fluid
                      variant="outline"
                      value={[]}
                      style={{ marginBottom: 5 }}
                      placeholder="Add filter..."
                      onChange={(e, { value }) =>
                        setSelectedFilterParameters([
                          ...selectedFilterParameters,
                          value as string,
                        ])
                      }
                      options={unusedFilterParameters.map((param) => ({
                        key: param._id,
                        value: param._id,
                        text: param.name,
                      }))}
                    />
                    <SelectableList
                      clearable
                      options={usedFilterParameters.map((param) => ({
                        key: param._id,
                        value: param._id,
                        text: param.name,
                        values: param.options,
                      }))}
                      onChange={({ value }) => {
                        setSelectedFilterParameters(
                          selectedFilterParameters.filter(
                            (param) => param !== value,
                          ),
                        );
                        updateFilters({
                          ideaFieldChoices: fieldChoices.filter(
                            (choice) => !choice.startsWith(String(value)),
                          ),
                        });
                      }}
                      onOptionChange={(optionValues) =>
                        updateFilters({ ideaFieldChoices: optionValues })
                      }
                      selectedValues={fieldChoices}
                    />
                  </>
                ) : null}
              </RowFlex>
            </OptionsContainer>
          </div>
          <div style={{ height: 32 }}></div>
        </div>
      </SlidingPane>
    );
  }
  return null;
}

export default ChallengeIdeaFilters;
